import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { nhost } from "@/lib/nhost";
import { useAuth } from "@/hooks/useAuth";

// 💬 The chat corner: a round button in the bottom-left of every page (not admin).
//   • Updates: your latest orders + (resellers) notifications — signed-in people only
//   • Help:    a free "quick answers" helper that matches your question to our FAQ
//   • Live Chat: message our team (guests too) — replies arrive here

type OrderUpdate = { reference: string | null; title: string; phone: string | null; amount: number; status: string; updated_at: string };
type Note = { id: string; type: string; title: string; message: string; read: boolean; created_at: string };
type Updates = { orders: OrderUpdate[]; notifications: Note[]; unread: number; is_reseller: boolean };

const SEEN_KEY = "mad_updates_seen_at";
const CHAT_ID_KEY = "mad_chat_cid";
const CHAT_TOKEN_KEY = "mad_chat_token";

type ChatMsg = { id: string; sender: "user" | "admin"; body: string; created_at: string };

function readStore(key: string): string {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}
function writeStore(key: string, value: string) {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* private mode: chat still works until the page closes */
  }
}

// ── Quick answers (edit these any time) ──
const FAQ: { q: string; words: string[]; a: string; link?: { to: string; label: string } }[] = [
  {
    q: "How long does delivery take?",
    words: ["long", "time", "delivery", "deliver", "arrive", "when", "minutes", "delay", "slow", "yet"],
    a: "Most bundles arrive within a few minutes. If yours is taking longer, our system checks it with the network automatically every few minutes. You can see its status on the Track Order page.",
    link: { to: "/track-order", label: "Track my order" },
  },
  {
    q: "How do I track my order?",
    words: ["track", "status", "reference", "where", "order", "check"],
    a: "Use your 7-digit order reference (like 0000123) on the Track Order page. If you're signed in, your latest orders also show in the Updates tab here.",
    link: { to: "/track-order", label: "Track Order page" },
  },
  {
    q: "I sent data to the wrong number",
    words: ["wrong", "number", "mistake", "reverse", "cancel", "refund"],
    a: "Once data has been delivered to a number, the network doesn't let us move it, so please double-check the number before paying. If the order is still 'Awaiting payment' or 'Being checked', email support@manayadatalink.com with your reference straight away.",
  },
  {
    q: "What does 'Being checked' mean?",
    words: ["checked", "hold", "being", "stuck", "pending", "problem"],
    a: "Your payment arrived, but something needs a quick look by our team (for example the supplier didn't confirm yet). Your money is safe and we'll send the data or sort it out. You don't need to pay again.",
  },
  {
    q: "Which networks do you sell?",
    words: ["network", "mtn", "telecel", "airteltigo", "vodafone", "airtel", "tigo"],
    a: "We sell data for MTN, Telecel and AirtelTigo.",
    link: { to: "/buy", label: "Buy data" },
  },
  {
    q: "How do I pay? Are there fees?",
    words: ["pay", "payment", "momo", "card", "paystack", "fee", "charge", "wallet"],
    a: "You can pay with Mobile Money or card through Paystack (a small 4% payment fee is added). If you top up your wallet (3% fee), buying from your wallet has no extra fee.",
    link: { to: "/wallet", label: "My wallet" },
  },
  {
    q: "How do I become a reseller?",
    words: ["reseller", "resell", "earn", "business", "commission", "markup", "sell", "link"],
    a: "Sign up, then become a reseller to get your own link. When people buy data through your link, you earn a markup once their order is delivered. You can withdraw your earnings from your dashboard.",
    link: { to: "/reseller", label: "Become a reseller" },
  },
  {
    q: "How do I withdraw my earnings?",
    words: ["withdraw", "withdrawal", "payout", "cash", "money", "balance"],
    a: "Resellers can request a withdrawal from their dashboard. Our team sends it to your MoMo and you'll get a note in the Updates tab when it's paid.",
    link: { to: "/dashboard", label: "My dashboard" },
  },
  {
    q: "What is the Monthly Challenge?",
    words: ["challenge", "monthly", "reward", "10gb", "prize", "win"],
    a: "Resellers who reach GH₵1,000 in delivered sales in a calendar month can pick a reward, like a free 10GB bundle.",
    link: { to: "/monthly-challenge", label: "Challenge rules" },
  },
  {
    q: "How do I get free data?",
    words: ["free", "gift", "code", "giveaway", "monday"],
    a: "On Mondays from 6AM to 9PM (Ghana time) you can redeem a gift code for free MTN 1GB. We share codes on our WhatsApp channel.",
    link: { to: "/free-data", label: "Redeem a code" },
  },
  {
    q: "I forgot my password",
    words: ["password", "forgot", "login", "sign", "account", "reset"],
    a: "Use 'Forgot password' on the login page and we'll email you a reset link. Check your spam folder if it doesn't arrive.",
    link: { to: "/forgot-password", label: "Reset password" },
  },
];

function bestAnswer(question: string) {
  const words = question.toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  let best: (typeof FAQ)[number] | null = null;
  let bestScore = 0;
  for (const f of FAQ) {
    const score = words.filter((w) => f.words.some((k) => w === k || (w.length > 3 && k.startsWith(w)))).length;
    if (score > bestScore) {
      best = f;
      bestScore = score;
    }
  }
  return bestScore > 0 ? best : null;
}

function timeAgo(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const STATUS_STYLE: Record<string, string> = {
  Delivered: "bg-primary-100 text-primary-700",
  "Added to wallet": "bg-primary-100 text-primary-700",
  Sending: "bg-sky-100 text-sky-700",
  "Being checked": "bg-amber-100 text-amber-800",
  Failed: "bg-red-100 text-red-700",
  "Awaiting payment": "bg-foreground-100 text-foreground-600",
};

type ChatLine = { from: "me" | "bot"; text: string; link?: { to: string; label: string } };

const ChatCorner = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"updates" | "help" | "chat">("help");
  const [updates, setUpdates] = useState<Updates | null>(null);
  const [badge, setBadge] = useState(0);
  const [lines, setLines] = useState<ChatLine[]>([
    { from: "bot", text: "Hi! 👋 Ask me a question, or tap one below." },
  ]);
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  // ── Live chat state ──
  const [chatId, setChatId] = useState<string>(() => readStore(CHAT_ID_KEY));
  const [chatToken, setChatToken] = useState<string>(() => readStore(CHAT_TOKEN_KEY));
  const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([]);
  const [chatStatus, setChatStatus] = useState<"open" | "closed">("open");
  const [chatUnread, setChatUnread] = useState(0);
  const [chatInput, setChatInput] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState("");
  const lastAtRef = useRef<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  const callChat = async (payload: Record<string, unknown>) => {
    try {
      const r = await nhost.functions.post("/live-chat", { ...payload, token: chatToken || undefined });
      return r.body as Record<string, unknown>;
    } catch (e: unknown) {
      const msg = (e as { body?: { error?: unknown } })?.body?.error;
      throw new Error(typeof msg === "string" ? msg : "Chat is having trouble. Please try again.");
    }
  };

  // Do I already have a chat? (signed in: from my account; guest: from my secret token)
  const resumeChat = useCallback(async () => {
    if (!user && !chatToken) return;
    try {
      const r = await nhost.functions.post("/live-chat", { action: "resume", token: chatToken || undefined });
      const conv = (r.body as { conversation?: { id: string; status: "open" | "closed"; unread: number } | null }).conversation;
      if (conv) {
        setChatId(conv.id);
        writeStore(CHAT_ID_KEY, conv.id);
        setChatStatus(conv.status);
        setChatUnread(conv.unread);
      }
    } catch {
      /* quiet */
    }
  }, [user, chatToken]);

  const fetchChat = useCallback(async () => {
    if (!chatId) return;
    try {
      const r = await nhost.functions.post("/live-chat", {
        action: "fetch",
        conversation_id: chatId,
        token: chatToken || undefined,
        after: lastAtRef.current,
      });
      const b = r.body as { messages?: ChatMsg[]; status?: "open" | "closed" };
      if (b.status) setChatStatus(b.status);
      if (b.messages && b.messages.length) {
        lastAtRef.current = b.messages[b.messages.length - 1].created_at;
        setChatMsgs((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...b.messages!.filter((m) => !seen.has(m.id))];
        });
      }
      setChatUnread(0);
    } catch {
      // The chat was removed or doesn't belong to this browser any more: start fresh
      setChatId("");
      writeStore(CHAT_ID_KEY, "");
    }
  }, [chatId, chatToken]);

  // Check for replies: every 5 seconds while the chat is open on screen, every minute otherwise
  useEffect(() => {
    const watching = open && tab === "chat";
    if (watching) {
      fetchChat();
      const t = setInterval(fetchChat, 5000);
      return () => clearInterval(t);
    }
    resumeChat();
    const t = setInterval(resumeChat, 60 * 1000);
    return () => clearInterval(t);
  }, [open, tab, fetchChat, resumeChat]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMsgs, open, tab]);

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    setChatBusy(true);
    setChatError("");
    try {
      if (!chatId) {
        const b = await callChat({ action: "start", name: guestName, contact: guestContact, message: text });
        const cid = String(b.conversation_id || "");
        if (b.token) {
          setChatToken(String(b.token));
          writeStore(CHAT_TOKEN_KEY, String(b.token));
        }
        setChatId(cid);
        writeStore(CHAT_ID_KEY, cid);
        lastAtRef.current = null;
        setChatMsgs([]);
      } else {
        await callChat({ action: "send", conversation_id: chatId, message: text });
      }
      setChatInput("");
      setChatStatus("open");
      setTimeout(fetchChat, 300);
    } catch (e) {
      setChatError(e instanceof Error ? e.message : "Couldn't send. Please try again.");
    } finally {
      setChatBusy(false);
    }
  };

  const loadUpdates = useCallback(async () => {
    if (!user) return;
    try {
      const r = await nhost.functions.post("/my-updates", {});
      const u = r.body as Updates & { success?: boolean };
      if (!u.success) return;
      setUpdates(u);
      const seen = Number(localStorage.getItem(SEEN_KEY) || 0);
      const changed = u.orders.filter((o) => new Date(o.updated_at).getTime() > seen && o.status !== "Awaiting payment").length;
      setBadge(u.unread + changed);
    } catch {
      /* the bell just stays quiet */
    }
  }, [user]);

  // Check for news now, then every 2 minutes (signed-in people only)
  useEffect(() => {
    if (!user) {
      setUpdates(null);
      setBadge(0);
      return;
    }
    loadUpdates();
    const t = setInterval(loadUpdates, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [user, loadUpdates]);

  // Opening the Updates tab counts as "seen"
  useEffect(() => {
    if (!open || tab !== "updates" || !user) return;
    localStorage.setItem(SEEN_KEY, String(Date.now()));
    setBadge(0);
    if (updates?.unread) {
      nhost.functions.post("/my-updates", { action: "mark_read" }).catch(() => {});
    }
  }, [open, tab, user, updates?.unread]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [lines, open]);

  const openCorner = () => {
    setOpen((o) => !o);
    if (!open && chatUnread > 0) setTab("chat");
    else if (!open && user && badge > 0) setTab("updates");
  };

  const ask = (question: string) => {
    const q = question.trim();
    if (!q) return;
    const found = bestAnswer(q);
    setLines((prev) => [
      ...prev,
      { from: "me", text: q },
      found
        ? { from: "bot", text: found.a, link: found.link }
        : {
            from: "bot",
            text: "Sorry, I don't know that one yet. Tap the Live Chat tab above to message our team, or email support@manayadatalink.com (add your order reference if you have one).",
          },
    ]);
    setInput("");
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-[calc(6rem+var(--bar-h,0px))] left-4 z-50 w-[340px] max-w-[calc(100vw-2rem)] h-[480px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl border border-foreground-100 flex flex-col overflow-hidden">
          {/* Header + tabs */}
          <div className="bg-primary-600 text-white px-4 pt-3 pb-0">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm">Man Aya DataLink</span>
              <button onClick={() => setOpen(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/15 cursor-pointer" aria-label="Close">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="flex gap-1">
              {user && (
                <button
                  onClick={() => setTab("updates")}
                  className={`px-3 py-2 text-xs font-semibold rounded-t-lg cursor-pointer ${tab === "updates" ? "bg-white text-primary-700" : "text-white/80 hover:text-white"}`}
                >
                  <i className="ri-notification-3-line mr-1"></i>Updates
                  {badge > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{badge}</span>}
                </button>
              )}
              <button
                onClick={() => setTab("help")}
                className={`px-3 py-2 text-xs font-semibold rounded-t-lg cursor-pointer ${tab === "help" ? "bg-white text-primary-700" : "text-white/80 hover:text-white"}`}
              >
                <i className="ri-question-answer-line mr-1"></i>Quick Help
              </button>
              <button
                onClick={() => setTab("chat")}
                className={`px-3 py-2 text-xs font-semibold rounded-t-lg cursor-pointer ${tab === "chat" ? "bg-white text-primary-700" : "text-white/80 hover:text-white"}`}
              >
                <i className="ri-chat-3-line mr-1"></i>Live Chat
                {chatUnread > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{chatUnread}</span>}
              </button>
            </div>
          </div>

          {/* Updates */}
          {tab === "updates" && user && (
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {!updates ? (
                <p className="text-xs text-foreground-400 text-center py-8">Loading...</p>
              ) : (
                <>
                  {updates.notifications.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Notifications</p>
                      <div className="space-y-2">
                        {updates.notifications.map((n) => (
                          <div key={n.id} className={`rounded-xl p-3 border ${n.read ? "border-foreground-100" : "border-primary-200 bg-primary-50/50"}`}>
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs font-bold text-foreground-900">{n.title}</p>
                              <span className="text-[10px] text-foreground-400 whitespace-nowrap">{timeAgo(n.created_at)}</span>
                            </div>
                            <p className="text-xs text-foreground-600 mt-0.5">{n.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Your latest orders</p>
                    {updates.orders.length === 0 ? (
                      <p className="text-xs text-foreground-400 py-4 text-center">No orders yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {updates.orders.map((o, i) => (
                          <div key={`${o.reference}-${i}`} className="rounded-xl p-3 border border-foreground-100">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-bold text-foreground-900">{o.title}</p>
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLE[o.status] || STATUS_STYLE["Awaiting payment"]}`}>
                                {o.status}
                              </span>
                            </div>
                            <p className="text-[11px] text-foreground-500 mt-0.5">
                              {o.phone ? `${o.phone} · ` : ""}GH₵{o.amount.toFixed(2)} · #{o.reference} · {timeAgo(o.updated_at)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Link to="/track-order" onClick={() => setOpen(false)} className="block text-center text-xs font-semibold text-primary-600 py-2">
                    Track any order →
                  </Link>
                </>
              )}
            </div>
          )}

          {/* Quick help */}
          {tab === "help" && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className={`flex ${l.from === "me" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] text-xs leading-relaxed px-3 py-2 rounded-2xl ${
                        l.from === "me" ? "bg-primary-500 text-white rounded-br-sm" : "bg-foreground-50 text-foreground-800 rounded-bl-sm"
                      }`}
                    >
                      {l.text}
                      {l.link && (
                        <Link to={l.link.to} onClick={() => setOpen(false)} className="block mt-1.5 font-bold text-primary-600">
                          {l.link.label} →
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
                {lines.length <= 3 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {FAQ.slice(0, 6).map((f) => (
                      <button
                        key={f.q}
                        onClick={() => ask(f.q)}
                        className="text-[11px] font-semibold border border-primary-200 text-primary-700 hover:bg-primary-50 px-2.5 py-1 rounded-full cursor-pointer"
                      >
                        {f.q}
                      </button>
                    ))}
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <div className="border-t border-foreground-100 p-2 flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") ask(input);
                  }}
                  placeholder="Type your question..."
                  maxLength={200}
                  className="flex-1 text-xs border border-foreground-200 rounded-xl px-3 py-2 focus:outline-none focus:border-primary-400"
                />
                <button onClick={() => ask(input)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary-500 hover:bg-primary-600 text-white cursor-pointer" aria-label="Send">
                  <i className="ri-send-plane-2-line"></i>
                </button>
              </div>
            </>
          )}

          {/* Live chat */}
          {tab === "chat" && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                <div className="text-[11px] text-foreground-500 bg-foreground-50 rounded-xl px-3 py-2">
                  Our team will reply here as soon as we can. You can close this window and come back later — your chat is saved.
                </div>
                {chatMsgs.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] text-xs leading-relaxed px-3 py-2 rounded-2xl whitespace-pre-wrap break-words ${
                        m.sender === "user" ? "bg-primary-500 text-white rounded-br-sm" : "bg-foreground-50 text-foreground-800 rounded-bl-sm"
                      }`}
                    >
                      {m.sender === "admin" && <span className="block text-[10px] font-bold text-primary-600 mb-0.5">Man Aya team</span>}
                      {m.body}
                      <span className={`block text-[9px] mt-1 ${m.sender === "user" ? "text-white/70" : "text-foreground-400"}`}>{timeAgo(m.created_at)}</span>
                    </div>
                  </div>
                ))}
                {chatId && chatStatus === "closed" && (
                  <p className="text-[11px] text-center text-foreground-400 py-1">This chat was closed. Send a message to open it again.</p>
                )}
                <div ref={chatEndRef} />
              </div>

              {!chatId && !user && (
                <div className="px-2 pt-2 grid grid-cols-2 gap-2 border-t border-foreground-100">
                  <input
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Your name"
                    maxLength={80}
                    className="text-xs border border-foreground-200 rounded-xl px-3 py-2 focus:outline-none focus:border-primary-400"
                  />
                  <input
                    value={guestContact}
                    onChange={(e) => setGuestContact(e.target.value)}
                    placeholder="Phone or email"
                    maxLength={120}
                    className="text-xs border border-foreground-200 rounded-xl px-3 py-2 focus:outline-none focus:border-primary-400"
                  />
                </div>
              )}
              {chatError && <p className="px-3 pt-1 text-[11px] text-red-600">{chatError}</p>}
              <div className={`p-2 flex gap-2 ${chatId || user ? "border-t border-foreground-100" : ""}`}>
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendChat();
                    }
                  }}
                  placeholder={chatId ? "Type a message..." : "How can we help?"}
                  maxLength={1000}
                  rows={1}
                  className="flex-1 text-xs border border-foreground-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:border-primary-400"
                />
                <button
                  onClick={sendChat}
                  disabled={chatBusy}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary-500 hover:bg-primary-600 text-white cursor-pointer disabled:opacity-50"
                  aria-label="Send"
                >
                  <i className={chatBusy ? "ri-loader-4-line animate-spin" : "ri-send-plane-2-line"}></i>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* The round button */}
      <button
        onClick={openCorner}
        className="fixed bottom-[calc(1.25rem+var(--bar-h,0px))] left-4 z-50 w-14 h-14 flex items-center justify-center rounded-full bg-primary-600 hover:bg-primary-700 text-white shadow-lg cursor-pointer transition-colors"
        aria-label="Help and updates"
      >
        <i className={`${open ? "ri-close-line" : "ri-customer-service-2-line"} text-2xl`}></i>
        {!open && badge + chatUnread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold">
            {badge + chatUnread > 9 ? "9+" : badge + chatUnread}
          </span>
        )}
      </button>
    </>
  );
};

export default ChatCorner;
